import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase, BACKEND_URL } from '../../supabaseClient';
import { 
  CreditCard, 
  FileText, 
  Download, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  ExternalLink,
  Receipt
} from 'lucide-react';
import './BillingHistory.css';

const BillingHistory = () => {
  const [searchParams] = useSearchParams();
  const [loadingStatus, setLoadingStatus] = useState('idle'); // idle | polling | success
  
  // Real Data State
  const [invoices, setInvoices] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  // --- 1. FETCH HISTORY ---
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch(`${BACKEND_URL}/api/payment/history`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setInvoices(data);
        }
      } catch (err) {
        console.error("Failed to load history:", err);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchHistory();
  }, [loadingStatus]);

  // --- 2. POLLING LOGIC ---
  useEffect(() => {
    const orderId = searchParams.get('order_id');

    if (orderId && loadingStatus === 'idle') {
      setLoadingStatus('polling');
      
      const verifyPayment = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) return;

          const response = await fetch(`${BACKEND_URL}/api/payment/verify`, {
             method: 'POST',
             headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${session.access_token}`
             },
             body: JSON.stringify({ orderId })
          });

          const data = await response.json();
          
          if (data.status === 'success') {
            setLoadingStatus('success');
            setTimeout(() => { window.location.href = '/account/billing'; }, 1500);
          } else {
             // Handle failure or keep polling
          }

        } catch (err) {
          console.error("Verification Error:", err);
        }
      };
      verifyPayment();
    }
  }, [searchParams]);

  // --- HELPERS ---
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: currency || 'INR'
    }).format(amount);
  };

  return (
    <div className="billing-page-container fade-in">
      
      {/* --- HEADER BANNER --- */}
      <div className="billing-banner">
        <div>
          <h1>Billing & History</h1>
          <p>Manage your payment methods and download past invoices.</p>
        </div>
        <div className="billing-icon-circle">
            <Receipt size={32} />
        </div>
      </div>

      {/* --- STATUS NOTIFICATIONS --- */}
      {loadingStatus === 'polling' && (
        <div className="status-banner info">
          <div className="spinner-icon"><Clock size={20} /></div>
          <p>Verifying payment status with the bank...</p>
        </div>
      )}

      {loadingStatus === 'success' && (
        <div className="status-banner success">
          <CheckCircle size={20} />
          <p><strong>Payment Confirmed!</strong> Your dashboard has been updated.</p>
        </div>
      )}

      {/* --- CARD 1: PAYMENT METHOD --- */}
      <div className="account-card">
        <div className="account-card-header">
          <div className="header-icon-row">
             <CreditCard className="header-icon" size={24} />
             <h2>Payment Method</h2>
          </div>
          <p>Manage how you pay for your subscription.</p>
        </div>
        
        <div className="account-card-content">
          <div className="payment-method-row">
            <div className="method-details">
              <div className="method-icon-box">
                 <CreditCard size={28} />
              </div>
              <div className="method-text">
                <strong>Cashfree Secure Checkout</strong>
                <p>Payments are encrypted and handled securely via Cashfree.</p>
              </div>
            </div>
            <Link to="/pricing" className="btn btn-secondary">
               Update Plan <ExternalLink size={16} style={{marginLeft:'8px'}}/>
            </Link>
          </div>
        </div>
      </div>

      {/* --- CARD 2: INVOICE HISTORY --- */}
      <div className="account-card">
        <div className="account-card-header">
          <div className="header-icon-row">
             <FileText className="header-icon" size={24} />
             <h2>Invoice History</h2>
          </div>
          <p>View and download your past billing statements.</p>
        </div>
        
        <div className="table-responsive">
          <table className="billing-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Plan Description</th>
                <th>Amount</th>
                <th>Status</th>
                <th style={{textAlign: 'right'}}>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingHistory ? (
                 <tr>
                    <td colSpan="5" className="text-center">
                        <div className="loading-row">Loading history...</div>
                    </td>
                 </tr>
              ) : invoices.length > 0 ? (
                invoices.map((inv) => (
                  <tr key={inv.order_id}>
                    <td><div className="date-cell">{formatDate(inv.created_at)}</div></td>
                    <td>
                      <div className="plan-cell">
                        <span className="plan-name">{inv.tier ? inv.tier.toUpperCase() : 'PLAN'}</span>
                        <span className="plan-cycle">{inv.billing_cycle || 'One-time purchase'}</span>
                      </div>
                    </td>
                    <td><div className="amount-cell">{formatCurrency(inv.amount, inv.currency)}</div></td>
                    <td>
                      <span className={`status-badge ${inv.status.toLowerCase()}`}>
                        {inv.status === 'PAID' && <CheckCircle size={12} />}
                        {inv.status === 'PENDING' && <Clock size={12} />}
                        {inv.status === 'FAILED' && <AlertCircle size={12} />}
                        {inv.status}
                      </span>
                    </td>
                    <td style={{textAlign: 'right'}}>
                      <button className="btn-icon-only" onClick={() => alert("Invoice PDF generation coming soon.")} title="Download PDF">
                        <Download size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    <div className="empty-state">
                        <FileText size={32} />
                        <p>No billing history found.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default BillingHistory;